import { addLog } from 'assets/components/feedback/Feedback';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { exportListToExcel } from 'assets/utils/file/Excel';
import { defaultLimit, defaultOffset, HttpStatus } from 'config/Api.Config';
import ClaimJobPaymentEmailTemplate from 'models/core/claimJobPaymentEmailTemplate/Model.ClaimJobPaymentEmailTemplate';
import User from 'models/core/user/Model.User';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import ClaimJobPaymentStatusTriggers from 'models/enums/ClaimJobPaymentStatusTriggers';
import claimJobPaymentExportApi from 'models/finance/claimJobPayment/export/Api.ClaimJobPayment.Export';
import ClaimJobPayment from 'models/finance/claimJobPayment/Model.ClaimJobPayment';
import claimJobPaymentEmailSendEmailApi from 'models/finance/claimJobPaymentEmail/sendEmail/Api.ClaimJobPaymentEmail.SendEmail';
import Distributor from 'models/sales/distributor/Model.Distributor';
import { useState } from 'react';
import { financeClaimJobPaymentIndexRoute } from './Finance.ClaimJobPayment.Index';

export enum ClaimJobPaymentAction {
  ADD = 'add',
  INFO = 'info',
  SEND_EMAIL = 'sendEmail',
  CANCEL = 'cancel',
}
export enum FinanceClaimJobPaymentInfoTab {
  INFO = 'info',
  INVOICES = 'invoices',
}
export type ClaimJobPaymentEmailAction = 'sendEmail';
export type VerifyClaimAction = Utils.VerifyExtends<
  Module.Finance.ClaimJobPayment.Actions,
  Utils.ValueOf<typeof ClaimJobPaymentAction>
>;
export function useClaimJobPaymentRouter() {
  return usePageRouter<Module.Finance.ClaimJobPayment.Params, Module.Finance.ClaimJobPayment.Query>({
    route: financeClaimJobPaymentIndexRoute,
  });
}
export default function useClaimJobPayment() {
  const pageRouter = useClaimJobPaymentRouter();
  const { lang } = getLocales();
  const { permissions, user } = useAuthStore();
  const [emailAction, setEmailAction] = useState<ClaimJobPaymentEmailAction>(undefined);

  const canView =
    permissions.CREATE_CLAIMJOBPAYMENT ||
    permissions.UPDATE_CLAIMJOBPAYMENTITEM_USBANK_PAYMENT ||
    permissions.CANCEL_CLAIMJOBPAYMENTITEM_USBANK_PAYMENT ||
    permissions.EDIT_CLAIMJOBPAYMENTITEM ||
    permissions.CANCEL_CLAIMJOBPAYMENT;

  //#region Api calls
  const listApi = useApi(
    {
      action: ClaimJobPayment.list,
      default: {
        limit: defaultLimit,
        offset: defaultOffset,
      },
      body: {
        ...pageRouter.query.list,
      },
      callback: (req) => {
        pageRouter.updateQuery({ ...pageRouter.query, list: req });
      },
      wait: !canView,
    },
    [JSON.stringify(pageRouter.query.list)]
  );

  const findApi = useApi(
    {
      action: ClaimJobPayment.find,
      wait: !canView || !pageRouter.params.id,
      body: {
        id: Number(pageRouter.params.id),
      },
    },
    [pageRouter.params.id]
  );

  const cancelApi = useApi({
    action: ClaimJobPayment.cancel,
    wait: true,
    callback: () => {
      pageRouter.updateParams({});
      listApi.execute((b) => b);
    },
  });
  //#endregion

  const reload = () => {
    findApi.execute((b) => b);
    listApi.execute((b) => b);
  };

  const userApi = useApi({ action: User.list, body: { isDisabled: false }, wait: true });
  const dealersApi = useApi({
    action: Dealer.list,
    default: {
      id: user?.dealers?.map((d) => d.id) ?? [],
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
    },
    wait: true,
  });

  const distributorsApi = useApi({ action: Distributor.list, wait: true });

  const emailTemplatesApi = useApi({
    action: ClaimJobPaymentEmailTemplate.list,
    body: {
      claimJobPaymentStatusTrigger: [ClaimJobPaymentStatusTriggers.CLAIMS_REMITTANCE_ADVICE],
    },
  });
  const emailTemplates = emailTemplatesApi.payload?.data;
  async function sendEmail(emailTemplate: Model.IClaimJobPaymentEmailTemplate) {
    const res = await claimJobPaymentEmailSendEmailApi({
      params: { claimJobPaymentId: parseInt(pageRouter.params.id) },
      body: emailTemplate,
    });

    if (res.status === HttpStatus.ok) {
      addLog({ success: lang.emailSuccessfullySent });
      pageRouter.updateParams({});
    } else {
      addLog({ error: res.message });
    }
  }

  async function exportToExcel() {
    await exportListToExcel(claimJobPaymentExportApi, pageRouter.query.list, lang.claimsPaymentHistory);
  }

  return {
    listApi,
    userApi,
    dealersApi,
    distributorsApi,
    pageRouter,
    permissions,
    selected: pageRouter.params.id ? findApi?.payload : null,
    canView,
    emailAction,
    emailTemplates,
    emailTemplatesApi,
    setEmailAction,
    sendEmail,
    exportToExcel,
    reload,
    cancelApi,
  };
}

export function useClaimJobPaymentProvider() {
  return useDataProvider<ReturnType<typeof useClaimJobPayment>>();
}
